import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { TextField, Box, IconButton, Tooltip } from '@mui/material';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import VariableInterpolationHelp from './VariableInterpolationHelp';

import type { Header } from '../dataProvider/types';

const headerNamePattern = /^(x-[a-z]+)|(authorization)$/;

export const getHeaderNameValidationMessage = (name: string): string => {
  if (name === '') {
    flowchartLogger.debug('Validation: Header name is empty');
    return 'callflows.nodes.http.headerNameErrorEmpty';
  }
  if (!headerNamePattern.test(name)) {
    flowchartLogger.debug('Validation: Header name is invalid', name);
    return 'callflows.nodes.http.headerNameErrorInvalid';
  }
  flowchartLogger.debug('Validation: Header name is valid', name);
  return '';
};

export const getHeaderValueValidationMessage = (value: string): string => {
  if (value === '') {
    flowchartLogger.debug('Validation: Header value is empty');
    return 'callflows.nodes.http.headerValueErrorEmpty';
  }
  flowchartLogger.debug('Validation: Header value is valid', value);
  return '';
};

export const isHeaderValid = (header: Header): boolean =>
  getHeaderNameValidationMessage(header.name) === '' && getHeaderValueValidationMessage(header.value) === '';

interface HeaderInputProps {
  header: Header;
  onChange: (header: Header) => void;
  onDelete: () => void;
}

const HeaderInput = ({ header, onChange, onDelete }: HeaderInputProps): React.ReactElement => {
  const translate = useTranslate();

  // Derived from props rather than kept in state, so a freshly added (empty) header
  // is flagged immediately instead of only after the user types in it.
  const nameValidationMessage = getHeaderNameValidationMessage(header.name);
  const valueValidationMessage = getHeaderValueValidationMessage(header.value);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange({ ...header, name: event.target.value });
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    flowchartLogger.debug('set header value', event.target.value);
    onChange({ ...header, value: event.target.value });
  };

  return (
    <Box display='flex' alignItems='start' gap={1}>
      <TextField
        label={translate('callflows.nodes.http.headerName')}
        value={header.name}
        onChange={handleNameChange}
        error={nameValidationMessage !== ''}
        helperText={nameValidationMessage === '' ? '' : translate(nameValidationMessage)}
        required
      />
      <Box display='flex' alignItems='start'>
        <TextField
          label={translate('callflows.nodes.http.headerValue')}
          value={header.value}
          onChange={handleValueChange}
          error={valueValidationMessage !== ''}
          helperText={valueValidationMessage === '' ? '' : translate(valueValidationMessage)}
          required
        />
        <VariableInterpolationHelp />
      </Box>
      <Tooltip title={translate('ra.action.delete')}>
        <IconButton color='error' onClick={onDelete}>
          <DeleteRoundedIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default HeaderInput;
